import { AlertTriangleIcon, ClockIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ConversationWindow } from './types'

interface ConversationWindowBannerProps {
	window: ConversationWindow | null
}

function formatTimeRemaining(expiresAt: Date): string {
	const now = new Date()
	const diff = expiresAt.getTime() - now.getTime()

	if (diff <= 0) {
		return ''
	}

	const hours = Math.floor(diff / (1000 * 60 * 60))
	const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

	if (hours > 0) {
		return `${hours}h ${minutes}m`
	}
	return `${minutes}m`
}

export function ConversationWindowBanner({ window }: ConversationWindowBannerProps) {
	const { t } = useTranslation('common')
	const [timeRemaining, setTimeRemaining] = useState('')

	useEffect(() => {
		if (!window?.isOpen || !window.expiresAt) {
			return
		}

		const updateTimer = () => {
			setTimeRemaining(formatTimeRemaining(window.expiresAt!))
		}

		updateTimer() // Compute immediately — the window data arrives after first render, so don't wait for the first tick.
		const interval = setInterval(updateTimer, 60000) // Update every minute

		return () => clearInterval(interval)
	}, [window])

	if (!window) {
		return null
	}

	const isExpired = window.isOpen && window.expiresAt != null && window.expiresAt.getTime() <= Date.now()

	if (window.isOpen && !isExpired) {
		return (
			<div className="flex items-center gap-2 px-4 py-2 bg-green-50 border-b border-green-100 text-sm text-green-700">
				<ClockIcon className="size-4 flex-shrink-0" />
				<span>{t('whatsapp.bannerWindowOpen', { time: timeRemaining })}</span>
			</div>
		)
	}

	if (window.isOpen && isExpired) {
		return (
			<div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-700">
				<AlertTriangleIcon className="size-4 flex-shrink-0" />
				<span>{t('whatsapp.bannerWindowExpired')}</span>
			</div>
		)
	}

	return (
		<div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-700">
			<AlertTriangleIcon className="size-4 flex-shrink-0" />
			<span>{t('whatsapp.bannerWindowClosed')}</span>
		</div>
	)
}
